import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Check, FileText, ListTodo, Loader2, Mail, Pencil, Receipt, RefreshCw, Sparkles, StickyNote } from 'lucide-react';
import RecordNotesPanel from '../RecordNotes/RecordNotesPanel';
import { toast } from 'sonner';
import TranscriptViewerModal from './TranscriptViewerModal';
import TranscriptTasksTab from './TranscriptTasksTab';
import TranscriptEditTab, { type TranscriptEditSaveState } from './TranscriptEditTab';
import { extractParticipantEmail } from '../../lib/transcriptParticipants';
import { parseSubscriberDate, subscriberDaysAgoLabel } from '../../lib/subscriberDates';
import { isOpenTaskStatus } from '../../lib/networkingTasks';

interface TranscriptRow {
  id: string;
  title: string;
  content: string;
  summary: string;
  meeting_date: string;
  participants: string[];
  tags: string[];
  created_at: string;
  updated_at: string;
  transcript_chunks_status?: string;
  transcript_chunks_indexed_at?: string | null;
}

type DetailTab = 'tasks' | 'transcript' | 'notes' | 'edit';

function tabFromParam(value: string | null): DetailTab | null {
  if (value === 'tasks' || value === 'transcript' || value === 'notes' || value === 'edit') return value;
  return null;
}

function formatMeetingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatMeetingDateWithRelative(iso: string): string {
  const formatted = formatMeetingDate(iso);
  const d = parseSubscriberDate(iso);
  if (!d) return formatted;
  return `${formatted} (${subscriberDaysAgoLabel(d)})`;
}

interface ParticipantSubscriber {
  email: string;
  subscriberId: string | null;
  loading: boolean;
}

const TAB_CLASS = (active: boolean) =>
  active
    ? 'border-emerald-600 text-emerald-700 dark:text-emerald-300 dark:border-emerald-400'
    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600';

function taskTabBadgeClass(active: boolean, hasOpen: boolean): string {
  if (active) {
    return hasOpen
      ? 'bg-emerald-600 text-white'
      : 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-200';
  }
  return hasOpen
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

function editAutoSaveMessage(state: TranscriptEditSaveState): string {
  switch (state.status) {
    case 'pending':
      return 'Unsaved changes…';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'error':
      return state.error ?? 'Save failed';
    default:
      return state.isDirty ? 'Unsaved changes…' : 'All changes saved';
  }
}

type ReceiptStep = {
  step?: string;
  ok?: boolean;
  skipped?: boolean;
  error?: string;
  detail?: Record<string, unknown>;
};

function receiptDetail(step: ReceiptStep): Record<string, unknown> {
  return step.detail ?? {};
}

function formatReceiptStep(step: ReceiptStep): string {
  const detail = receiptDetail(step);
  if (!step.ok) {
    const label =
      step.step === 'index-transcript-chunks'
        ? 'Index'
        : step.step === 'create_transcript_review_task'
          ? 'Review task'
          : step.step === 'extract-transcript-tasks'
            ? 'Tasks'
            : step.step === 'extract-orbit-followup'
              ? 'Orbit'
              : step.step === 'person-chats'
                ? 'Chats'
                : 'Receipt';
    return `${label} failed: ${step.error ?? 'unknown error'}`;
  }

  switch (step.step) {
    case 'index-transcript-chunks': {
      const count = detail.chunk_count;
      return typeof count === 'number' ? `Indexed ${count} chunks` : 'Indexed';
    }
    case 'create_transcript_review_task':
      return 'Review task ready';
    case 'extract-transcript-tasks': {
      if (step.skipped) return 'Tasks skipped';
      const created = typeof detail.created_count === 'number' ? detail.created_count : 0;
      const skipped = typeof detail.skipped_count === 'number' ? detail.skipped_count : 0;
      return `Tasks: ${created} created, ${skipped} skipped`;
    }
    case 'extract-orbit-followup': {
      if (step.skipped || detail.skipped === true) {
        const reason = typeof detail.reason === 'string' ? detail.reason : '';
        if (!reason || reason.includes('ORBIT_FOLLOWUP')) return 'Orbit skipped';
        return `Orbit skipped: ${reason}`;
      }
      return detail.held_email_id ? 'Orbit held email created' : 'Orbit finished';
    }
    case 'person-chats': {
      const filed = typeof detail.filed === 'number' ? detail.filed : 0;
      const guests = typeof detail.guests === 'number' ? detail.guests : 0;
      return `Chats updated: ${filed} of ${guests}`;
    }
    default:
      return step.step ?? 'Receipt step';
  }
}

function editAutoSaveClass(state: TranscriptEditSaveState): string {
  switch (state.status) {
    case 'pending':
      return 'text-amber-600 dark:text-amber-400';
    case 'saving':
      return 'text-gray-600 dark:text-gray-300';
    case 'saved':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'error':
      return 'text-red-600 dark:text-red-400';
    default:
      return state.isDirty
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-gray-500 dark:text-gray-400';
  }
}

const TranscriptDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transcript, setTranscript] = useState<TranscriptRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [orbitBusy, setOrbitBusy] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const receiptLock = useRef(false);
  const [activeTab, setActiveTab] = useState<DetailTab>(() => tabFromParam(searchParams.get('tab')) ?? 'tasks');
  const [participantSubscribers, setParticipantSubscribers] = useState<Map<string, ParticipantSubscriber>>(new Map());
  const [taskCounts, setTaskCounts] = useState({ total: 0, open: 0 });
  const [editSaveState, setEditSaveState] = useState<TranscriptEditSaveState>({
    status: 'idle',
    error: null,
    isDirty: false,
  });

  const loadTaskCounts = useCallback(async (transcriptId: string) => {
    const { data, error } = await supabase.from('tasks').select('status').eq('transcript_id', transcriptId);
    if (error) return;
    const rows = data ?? [];
    setTaskCounts({
      total: rows.length,
      open: rows.filter((row) => isOpenTaskStatus(row.status)).length,
    });
  }, []);

  const load = useCallback(async () => {
    if (!id?.trim()) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const { data, error } = await supabase.from('transcripts').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      if (!data) {
        setTranscript(null);
        setNotFound(true);
        return;
      }
      setTranscript(data as TranscriptRow);

      if (data.participants && Array.isArray(data.participants)) {
        await checkParticipantSubscribers(data.participants);
      }
      await loadTaskCounts(data.id);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load transcript');
      setTranscript(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, loadTaskCounts]);

  const checkParticipantSubscribers = async (participants: string[]) => {
    const subscriberMap = new Map<string, ParticipantSubscriber>();

    for (const participant of participants) {
      const email = extractParticipantEmail(participant);
      if (email) {
        const { data } = await supabase
          .from('subscribers')
          .select('id')
          .ilike('email', email)
          .maybeSingle();

        subscriberMap.set(participant, {
          email,
          subscriberId: data?.id ? String(data.id) : null,
          loading: false,
        });
      }
    }

    setParticipantSubscribers(subscriberMap);
  };

  const convertToSubscriber = async (participant: string) => {
    const info = participantSubscribers.get(participant);
    if (!info || info.subscriberId) return;

    setParticipantSubscribers((prev) => {
      const next = new Map(prev);
      const current = next.get(participant);
      if (current) {
        next.set(participant, { ...current, loading: true });
      }
      return next;
    });

    try {
      const namePart = participant.replace(info.email, '').trim().replace(/[<>()]/g, '').trim();
      const fullName = namePart || info.email.split('@')[0];

      const { data, error } = await supabase
        .from('subscribers')
        .insert({
          email: info.email,
          name: fullName,
          email_status: 'raw',
          funnel_status: 'prospect',
          source: 'transcript',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) throw error;

      setParticipantSubscribers((prev) => {
        const next = new Map(prev);
        next.set(participant, {
          ...info,
          subscriberId: String(data.id),
          loading: false,
        });
        return next;
      });

      toast.success(`Added ${fullName} as a subscriber`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to create subscriber');
      setParticipantSubscribers((prev) => {
        const next = new Map(prev);
        const current = next.get(participant);
        if (current) {
          next.set(participant, { ...current, loading: false });
        }
        return next;
      });
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const tab = tabFromParam(searchParams.get('tab'));
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'edit') {
      setEditSaveState({ status: 'idle', error: null, isDirty: false });
    }
  }, [activeTab]);

  const runReceipt = async () => {
    if (receiptLock.current || !id?.trim()) return;
    receiptLock.current = true;
    setReceiptBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        steps?: ReceiptStep[];
      }>('process-transcript-receipt', {
        body: { transcript_id: id },
      });
      if (error) throw new Error(error.message || 'Receipt failed');
      if (data?.error && !data.steps?.length) throw new Error(data.error);
      const steps = data?.steps ?? [];
      const summary = steps.map(formatReceiptStep).join(' · ') || 'Receipt finished';
      if (steps.some((step) => !step.ok)) toast.error(summary);
      else toast.success(summary);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Receipt failed');
    } finally {
      receiptLock.current = false;
      setReceiptBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto flex flex-col items-center justify-center py-24 gap-3 text-gray-500 dark:text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
        <p className="text-sm">Loading transcript…</p>
      </div>
    );
  }

  if (notFound || !transcript) {
    return (
      <div className="max-w-7xl mx-auto">
        <Link
          to="/transcripts"
          className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to transcripts
        </Link>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center text-gray-600 dark:text-gray-400">
          Transcript not found.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Link
        to="/transcripts"
        className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to transcripts
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
            <FileText className="w-7 h-7" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{transcript.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {formatMeetingDateWithRelative(transcript.meeting_date)}
            </p>
            {activeTab === 'edit' ? (
              <p className={`text-xs mt-1 inline-flex items-center gap-1.5 ${editAutoSaveClass(editSaveState)}`}>
                {editSaveState.status === 'saving' ? (
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                ) : editSaveState.status === 'saved' ? (
                  <Check className="w-3 h-3 shrink-0" />
                ) : null}
                {editAutoSaveMessage(editSaveState)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={orbitBusy}
            onClick={async () => {
              if (!transcript?.id) return;
              setOrbitBusy(true);
              try {
                const { data, error } = await supabase.functions.invoke<{
                  error?: string;
                  skipped?: boolean;
                  reason?: string;
                  held_email_id?: string;
                  ok?: boolean;
                }>('extract-orbit-followup', {
                  body: { transcript_id: transcript.id, force: false },
                });
                if (error) throw new Error(error.message || 'Orbit extract failed');
                if (data?.error) throw new Error(data.error);
                if (data?.skipped) {
                  toast.message(`Skipped: ${data.reason ?? 'already exists or no match'}`);
                  if (data.held_email_id) navigate(`/held-emails/${data.held_email_id}`);
                  return;
                }
                if (data?.held_email_id) {
                  toast.success('Held orbit email created');
                  navigate(`/held-emails/${data.held_email_id}`);
                } else {
                  toast.message('Orbit extract finished with no held email');
                }
              } catch (e: unknown) {
                toast.error(e instanceof Error ? e.message : 'Orbit extract failed');
              } finally {
                setOrbitBusy(false);
              }
            }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
          >
            {orbitBusy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Orbit held email
          </button>
          <button
            type="button"
            disabled={receiptBusy}
            onClick={() => {
              if (receiptBusy) return;
              void runReceipt();
            }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50"
          >
            {receiptBusy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Receipt className="w-4 h-4 mr-2" />
            )}
            Run receipt
          </button>
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/50 text-violet-900 dark:text-violet-100 hover:bg-violet-100 dark:hover:bg-violet-900/40"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Chat
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6" aria-label="Transcript sections">
          <button
            type="button"
            onClick={() => setActiveTab('tasks')}
            className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${TAB_CLASS(activeTab === 'tasks')}`}
          >
            <ListTodo className="w-4 h-4" />
            Tasks
            {taskCounts.total > 0 ? (
              <span
                className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold leading-none ${taskTabBadgeClass(activeTab === 'tasks', taskCounts.open > 0)}`}
                title={
                  taskCounts.open > 0
                    ? `${taskCounts.open} open · ${taskCounts.total} total`
                    : `${taskCounts.total} task${taskCounts.total === 1 ? '' : 's'}`
                }
              >
                {taskCounts.open > 0 ? taskCounts.open : taskCounts.total}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('transcript')}
            className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${TAB_CLASS(activeTab === 'transcript')}`}
          >
            <FileText className="w-4 h-4" />
            Transcript
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notes')}
            className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${TAB_CLASS(activeTab === 'notes')}`}
          >
            <StickyNote className="w-4 h-4" />
            Notes
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('edit')}
            className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${TAB_CLASS(activeTab === 'edit')}`}
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        </nav>
      </div>

      {activeTab === 'tasks' ? (
        <TranscriptTasksTab
          transcriptId={transcript.id}
          summary={transcript.summary}
          participants={transcript.participants}
          participantSubscribers={participantSubscribers}
          onConvertToSubscriber={(p) => void convertToSubscriber(p)}
          onParticipantsUpdated={() => void load()}
          onTasksChanged={() => void loadTaskCounts(transcript.id)}
        />
      ) : activeTab === 'notes' ? (
        <RecordNotesPanel recordType="transcript" recordId={transcript.id} />
      ) : activeTab === 'transcript' ? (
        <div className="space-y-6">
          {transcript.tags?.length ? (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Tags
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {transcript.tags.map((tag, i) => (
                  <span
                    key={`${i}-${tag}`}
                    className="inline-flex px-2.5 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Full transcript
            </h2>
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 max-h-[min(70vh,50rem)] overflow-y-auto">
              {transcript.content?.trim() || '—'}
            </pre>
          </section>
        </div>
      ) : (
        <TranscriptEditTab
          transcript={transcript}
          onSaved={() => void load()}
          onSaveStatusChange={setEditSaveState}
        />
      )}

      <TranscriptViewerModal
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        transcript={transcript}
        onIndexSuccess={() => void load()}
      />
    </div>
  );
};

export default TranscriptDetailView;
